import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Tables } from '@/integrations/supabase/types';

type GuestAccess = Tables<'guest_access'>;
type Booking = Tables<'booking_core'>;

interface GuestSession {
  guest: GuestAccess;
  booking: Booking;
}

export const useGuestAuth = () => {
  const [guestSession, setGuestSession] = useState<GuestSession | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const guestLogin = async (accessCode: string) => {
    try {
      setLoading(true);
      
      // Set the access code in the session for RLS
      await supabase.rpc('set_config', {
        parameter: 'app.guest_access_code',
        value: accessCode
      });

      // Get guest access data - temporary simplified version
      const { data: guestAccess, error: accessError } = await supabase
        .from('guest_access')
        .select('*')
        .eq('access_code', accessCode)
        .eq('is_active', true)
        .single();

      if (accessError || !guestAccess) {
        throw new Error('Неверный код доступа или доступ заблокирован');
      }

      // Update last login time
      await supabase
        .from('guest_access')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', guestAccess.id);

      // Get booking separately for now
      const { data: booking } = await supabase
        .from('booking_core')
        .select('*')
        .eq('id', guestAccess.booking_id)
        .single();

      const session: GuestSession = {
        guest: guestAccess,
        booking: booking as Booking
      };

      setGuestSession(session);
      localStorage.setItem('guestAccessCode', accessCode);
      
      toast({
        title: "Добро пожаловать!",
        description: `Здравствуйте, Гость!`,
      });

      return { session, error: null };
    } catch (error: any) {
      console.error('Guest login error:', error);
      toast({
        title: "Ошибка входа",
        description: error.message || "Не удалось войти в систему",
        variant: "destructive",
      });
      return { session: null, error };
    } finally {
      setLoading(false);
    }
  };

  const guestLogout = async () => {
    setGuestSession(null);
    localStorage.removeItem('guestAccessCode');
    
    // Clear the access code from session
    await supabase.rpc('set_config', {
      parameter: 'app.guest_access_code',
      value: ''
    });

    toast({
      title: "Выход выполнен",
      description: "До свидания!",
    });
  };

  const checkGuestSession = async () => {
    try {
      setLoading(true);
      const accessCode = localStorage.getItem('guestAccessCode');
      
      if (!accessCode) {
        setGuestSession(null);
        return;
      }

      await guestLogin(accessCode);
    } catch (error) {
      console.error('Session check error:', error);
      localStorage.removeItem('guestAccessCode');
      setGuestSession(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkGuestSession();
  }, []);

  return {
    guestSession,
    loading,
    guestLogin,
    guestLogout,
    isAuthenticated: !!guestSession
  };
};